"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import { Badge } from "@/components/ui/badge";
import { STAKE_CONSTANTS } from "@/lib/stake/stake-constants";
import { cn } from "@/lib/utils";

interface DurationChipRowProps {
  selectedDurationSec: bigint | null;
  onChange: (dur: bigint) => void;
}

const DURATION_OPTIONS = [
  { key: "duration12m" as const, value: STAKE_CONSTANTS.ONE_YEAR_SECONDS },
  {
    key: "duration18m" as const,
    value: STAKE_CONSTANTS.EIGHTEEN_MONTH_SECONDS,
  },
  { key: "duration24m" as const, value: STAKE_CONSTANTS.TWO_YEAR_SECONDS },
] as const;

export const DurationChipRow: React.FC<DurationChipRowProps> = ({
  selectedDurationSec,
  onChange,
}) => {
  const t = useTranslations("stake");
  return (
    <div
      role="radiogroup"
      aria-label={t("modal.durationAriaLabel")}
      // Default RTL flex — chips align to the start (right in RTL).
      className="flex gap-2"
    >
      {DURATION_OPTIONS.map((opt) => {
        const isSelected = selectedDurationSec === opt.value;
        const label = t(`modal.${opt.key}`);
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(opt.value)}
            className="cursor-pointer p-0 bg-transparent border-0"
          >
            <Badge
              shape="rounded-lg"
              variant={isSelected ? "secondary" : "solid"}
              size="md"
              className={cn(
                "w-[67px] h-8 text-sm cursor-pointer transition-colors",
                isSelected
                  ? "bg-bg-elevated text-text-primary"
                  : "bg-bg-elevated text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/70",
              )}
            >
              {label}
            </Badge>
          </button>
        );
      })}
    </div>
  );
};
